import React, { ReactElement, memo } from 'react';
import { Button, Row, Cell, Grid, Paragraph, Icon, Switch, colors, Tag, Tooltip } from '@dexma/ui-components';

import i18n from 'asset/translation';
import gtmIds from 'adapter/in/dashboards/config/gtm';
import {
    StyledCopyBody,
    StyledDashboardLinkModal,
    StyledDisabledInputLink,
    StyledDivAnimationResetLink,
    StyledDivExternalAccess,
    StyledResetLinks,
    StyledResetLinksDescription,
    StyledRowContainerRow,
    StyledRowMarginTop,
    StyledSeparatorLine,
    StyledSpanFontWeight,
    StyledSpanPointer,
    StyledSpanTitleTooltipCopy,
    StyledSpanTitleTooltipReset,
    StyledSwitchAccess,
    StyledTagGroup,
} from 'adapter/in/dashboards/pages/Modals/LinkModal/style';
import ServerErrorAlert from 'adapter/in/dashboards/components/ServerErrorAlert';
import Link from 'domain/entity/link';

const gtm = gtmIds.dashboard.linkModal;

export type DashboardLinkModalProps = {
    disabledButtons: boolean;
    error: boolean;
    isLoading: boolean;
    isChecked?: boolean;
    link?: Link;
    showModal: boolean;
    showResetContainer: boolean;
    showResetTooltip: boolean;
    showLinkTooltip: boolean;
    showEmbedTooltip: boolean;
    tags: { label: string; icon: string }[];
    handleCloseModal: () => void;
    handleCheckSwitch: (value: boolean) => void;
    handleResetLink: () => void;
    handleCopyLink: () => void;
    handleCopyHTML: () => void;
    handleShowResetActionModal: (value: boolean) => void;
    handleCSVLinksDownload: () => void;
    onTryAgain: () => void;
};

type SuccesfulCopyTooltipTitleProps = {
    title: string;
};

const SuccesfulCopyTooltipTitle = ({ title }: SuccesfulCopyTooltipTitleProps) => (
    <StyledCopyBody>
        <Icon name='check' size='small' color='white' />
        <StyledSpanTitleTooltipCopy>{title}</StyledSpanTitleTooltipCopy>
    </StyledCopyBody>
);

const DashboardLinkModal = memo(
    (props: DashboardLinkModalProps): ReactElement => {
        const {
            disabledButtons,
            error,
            isLoading,
            isChecked,
            link,
            showModal,
            showResetContainer,
            showResetTooltip,
            showLinkTooltip,
            showEmbedTooltip,
            tags,
            handleCopyLink,
            handleCopyHTML,
            handleResetLink,
            handleCheckSwitch,
            handleCloseModal,
            handleShowResetActionModal,
            handleCSVLinksDownload,
            onTryAgain,
        } = props;
        let dataTestId = 'link-copy';
        if (isLoading) dataTestId = 'loading';

        return (
            <>
                <StyledDashboardLinkModal
                    width={547}
                    open={showModal}
                    onCancel={handleCloseModal}
                    title={i18n.t('dashboards.actionDashboardLink')}
                    footer={null}
                >
                    <Grid fluid className='no-padding-grid' data-testid='link-dashboard-modal'>
                        {error && (
                            <Row>
                                <Cell>
                                    <ServerErrorAlert tryAgain={onTryAgain} dataId={`${gtm.actions.tryAgain}`} />
                                </Cell>
                            </Row>
                        )}
                        <StyledDivExternalAccess>
                            <StyledSpanFontWeight>{i18n.t('dashboards.link.access.title')}</StyledSpanFontWeight>
                            <StyledSwitchAccess>
                                <Switch
                                    size='small'
                                    defaultChecked
                                    checked={isChecked}
                                    data-testid='switch-enable-link'
                                    onChange={(value: boolean) => handleCheckSwitch(value)}
                                />
                                <span data-testid='text-enable-link'>
                                    {isChecked
                                        ? i18n.t('dashboards.link.access.label.enabled')
                                        : i18n.t('dashboards.link.access.label.disabled')}
                                </span>
                            </StyledSwitchAccess>
                            <StyledResetLinks>
                                <Icon size='small' name='refresh' color={colors.red} />
                                <StyledSpanPointer
                                    data-testid='reset-title'
                                    onClick={() => handleShowResetActionModal(!showResetContainer)}
                                >
                                    {i18n.t('dashboards.link.access.reset.title')}
                                </StyledSpanPointer>
                            </StyledResetLinks>
                            <StyledDivAnimationResetLink $isOpen={showResetContainer}>
                                <StyledResetLinksDescription>
                                    <Icon name='warning' size='medium' />
                                    <Paragraph>{i18n.t('dashboards.link.access.reset.description')}</Paragraph>
                                </StyledResetLinksDescription>
                                <StyledResetLinks>
                                    <Button
                                        variant='secondary'
                                        size='medium'
                                        text={i18n.t('common.cancel')}
                                        data-testid='dashboard-linkModal-cancel-reset'
                                        onClick={() => handleShowResetActionModal(false)}
                                    />
                                    <Button
                                        variant='destructive'
                                        size='medium'
                                        text={i18n.t('dashboards.link.access.reset.action')}
                                        data-testid='dashboard-linkModal-reset'
                                        onClick={() => handleResetLink()}
                                        dataId={gtm.actions.reset}
                                    />
                                </StyledResetLinks>
                            </StyledDivAnimationResetLink>
                        </StyledDivExternalAccess>
                        <StyledRowContainerRow>
                            <Cell>
                                <StyledSpanFontWeight>{i18n.t('dashboards.link.label')}</StyledSpanFontWeight>
                            </Cell>
                            <Cell>
                                <Tooltip
                                    arrow={false}
                                    position='right'
                                    open={showResetTooltip}
                                    color={colors.green}
                                    title={() => (
                                        <StyledCopyBody>
                                            <Icon name='check' size='medium' color='white' />
                                            <StyledSpanTitleTooltipReset>
                                                {i18n.t('dashboards.link.access.reset.success')}
                                            </StyledSpanTitleTooltipReset>
                                        </StyledCopyBody>
                                    )}
                                    trigger='click'
                                />
                            </Cell>
                        </StyledRowContainerRow>
                        <StyledRowMarginTop>
                            <Cell>
                                <StyledDisabledInputLink type='text' value={link?.linkedUrl} disabled />
                            </Cell>
                        </StyledRowMarginTop>
                        <StyledRowMarginTop>
                            <Cell>
                                <Paragraph data-testid='link-message' size='small' color='gray500'>
                                    {i18n.t('dashboards.link.tagsLabel')}
                                </Paragraph>
                            </Cell>
                        </StyledRowMarginTop>
                        <StyledTagGroup>
                            {tags.map((tag, id) => (
                                <Tag key={id} variant='outline' color='gray500' icon={tag.icon}>
                                    {tag.label}
                                </Tag>
                            ))}
                        </StyledTagGroup>
                        <Row>
                            <Tooltip
                                arrow={false}
                                open={showLinkTooltip}
                                color={colors.green}
                                title={() => <SuccesfulCopyTooltipTitle title={i18n.t('dashboards.link.copy.success')} />}
                                trigger='click'
                            >
                                <Button
                                    variant='link'
                                    size='small'
                                    text={i18n.t('dashboards.link.copy.title')}
                                    data-testid={dataTestId}
                                    isLoading={isLoading}
                                    disabled={disabledButtons}
                                    iconBefore='link'
                                    onClick={() => handleCopyLink()}
                                    dataId={gtm.actions.copy}
                                />
                            </Tooltip>
                            <Tooltip
                                arrow={false}
                                open={showEmbedTooltip}
                                color={colors.green}
                                title={() => <SuccesfulCopyTooltipTitle title={i18n.t('dashboards.link.embedCode.success')} />}
                                trigger='click'
                            >
                                <Button
                                    variant='link'
                                    size='small'
                                    text={i18n.t('dashboards.link.embedCode.title')}
                                    data-testid={`${dataTestId}-html-code`}
                                    isLoading={isLoading}
                                    disabled={disabledButtons}
                                    iconBefore='widget_iframe'
                                    onClick={() => handleCopyHTML()}
                                    dataId={gtm.actions.copy}
                                />
                            </Tooltip>
                        </Row>
                        <StyledSeparatorLine />
                        <StyledRowMarginTop>
                            <Cell>
                                <StyledSpanFontWeight>{i18n.t('dashboards.link.bulk.title')}</StyledSpanFontWeight>
                            </Cell>
                        </StyledRowMarginTop>
                        <StyledRowMarginTop>
                            <Cell>
                                <Paragraph data-testid='bulk-message' size='small' color='gray500'>
                                    {i18n.t('dashboards.link.bulk.description')}
                                </Paragraph>
                            </Cell>
                        </StyledRowMarginTop>
                        <StyledRowMarginTop>
                            <Cell>
                                <Button
                                    size='small'
                                    text={i18n.t('dashboards.link.bulk.download')}
                                    data-testid={`${dataTestId}-bulk-download`}
                                    isLoading={isLoading}
                                    // disabled={disabledButtons}
                                    iconBefore='download'
                                    onClick={() => {
                                        handleCSVLinksDownload();
                                    }}
                                    dataId={gtm.actions.bulk}
                                />
                            </Cell>
                        </StyledRowMarginTop>
                    </Grid>
                </StyledDashboardLinkModal>
            </>
        );
    }
);

export default DashboardLinkModal;
